export const PORTAL_VERSION_STORAGE_KEY =
  import.meta.env.VITE_PORTAL_VERSION_STORAGE_KEY || "king-energy-portal-version";

/** Prevents rapid / ↔ /v2/ preference ping-pong within one tab session. */
export const PORTAL_REDIRECT_GUARD_KEY =
  import.meta.env.VITE_PORTAL_REDIRECT_GUARD_KEY || "king-energy-portal-redirect-guard";
export const PORTAL_REDIRECT_GUARD_MS = 15_000;

/** One-shot reload when v1 SPA handles `/v2/*` instead of the edge rewrite. */
export const V2_RELOAD_KEY = "portal-v2-rewrite-reload";

export type PortalVersion = "v1" | "v2";

/** Classic sidebar "Try the new portal". Off unless explicitly true. */
export const showV1ToV2Link =
  import.meta.env.VITE_SHOW_V1_TO_V2_LINK === "true";

/**
 * Auto-send classic entry routes to v2 when localStorage prefers v2.
 * On by default. Set `VITE_PORTAL_VERSION_AUTO_REDIRECT=false` to disable
 * (explicit false only — unset keeps redirect).
 * Skips Auth0 callback URLs, register-return `invitationCode`, and iframe
 * impersonation. Preference redirects keep the current query string.
 *
 * Only v1 → v2 is automatic. v2 → v1 is button-only so Auth0 / Vite base
 * `/v2/` cannot ping-pong with preference redirects.
 */
export const portalVersionAutoRedirect =
  import.meta.env.VITE_PORTAL_VERSION_AUTO_REDIRECT !== "false";

function isImpersonating(): boolean {
  return window.location !== window.parent.location;
}

/** Auth0 callback query params — never preference-redirect while these are present. */
export function hasAuth0CallbackParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  // Require `code` or `error` — bare `state` is too broad and can block the
  // early v2 redirect while Auth0 silent auth sits on "Loading..." (~60s).
  return params.has("code") || params.has("error") || params.has("error_description");
}

/**
 * Register-return invite token. After Auth0 strips `code`/`state`, the URL is
 * `/?invitationCode=…` and account linking still needs that token on classic.
 */
export function hasInvitationCode(): boolean {
  const value = new URLSearchParams(window.location.search).get("invitationCode");
  return Boolean(value);
}

/** Classic register-return path Auth0 should land on after signup. */
export function registerReturnPath(invitationCode: string): string {
  const params = new URLSearchParams();
  params.set("invitationCode", invitationCode);
  return `/?${params.toString()}`;
}

/**
 * URL Auth0 should leave in the address bar after exchanging `code`.
 * The SDK default is `appState.returnTo || pathname`, which drops `invitationCode`.
 */
export function locationAfterAuth0Callback(appState?: { returnTo?: string }): string {
  const returnTo = appState?.returnTo?.trim();
  if (returnTo) {
    return returnTo;
  }
  const params = new URLSearchParams(window.location.search);
  params.delete("code");
  params.delete("state");
  params.delete("error");
  params.delete("error_description");
  const search = params.toString();
  const hash = window.location.hash || "";
  return `${window.location.pathname}${search ? `?${search}` : ""}${hash}`;
}

export function portalOrigin(): string {
  return window.location.origin;
}

/** Public v2 URL on the current host (`origin` + `/v2` + optional path). */
export function portalV2Url(path = "/"): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === "/" || normalized === "") {
    // Trailing slash matches Auth0 / Vite base contract (bare `/v2` redirects).
    return `${portalOrigin()}/v2/`;
  }
  if (normalized.startsWith("/v2")) {
    return `${portalOrigin()}${normalized}`;
  }
  return `${portalOrigin()}/v2${normalized}`;
}

/** Canonical v2 entry URL (trailing slash so hosted rewrites match). */
export function portalV2EntryUrl(): string {
  return `${portalOrigin()}/v2/`;
}

/** v2 entry plus the current query string. */
export function portalV2PreferenceRedirectUrl(): string {
  return `${portalV2EntryUrl()}${window.location.search}`;
}

export function getPreferredPortalVersion(): PortalVersion | null {
  const value = localStorage.getItem(PORTAL_VERSION_STORAGE_KEY);
  if (value === "v1" || value === "v2") {
    return value;
  }
  return null;
}

export function setPreferredPortalVersion(version: PortalVersion): void {
  localStorage.setItem(PORTAL_VERSION_STORAGE_KEY, version);
}

function clearPortalRedirectGuard(): void {
  sessionStorage.removeItem(PORTAL_REDIRECT_GUARD_KEY);
}

/** True if a preference redirect already ran recently (skip to avoid loops). */
function shouldSkipPortalRedirect(): boolean {
  const raw = sessionStorage.getItem(PORTAL_REDIRECT_GUARD_KEY);
  if (!raw) {
    return false;
  }
  const then = Number(raw);
  if (!Number.isFinite(then)) {
    sessionStorage.removeItem(PORTAL_REDIRECT_GUARD_KEY);
    return false;
  }
  return Date.now() - then < PORTAL_REDIRECT_GUARD_MS;
}

function markPortalRedirect(): void {
  sessionStorage.setItem(PORTAL_REDIRECT_GUARD_KEY, String(Date.now()));
}

export function goToPortalV2(): void {
  setPreferredPortalVersion("v2");
  clearPortalRedirectGuard();
  window.location.assign(portalV2EntryUrl());
}

/** Mirror of v2 `goToPortalV1` — kept for API parity; classic UI uses v2's "Back to classic". */
export function goToPortalV1(): void {
  setPreferredPortalVersion("v1");
  clearPortalRedirectGuard();
  window.location.assign(`${portalOrigin()}/`);
}

/**
 * Entry routes where a stored v2 preference may auto-redirect before the shell loads.
 * Deep classic routes (e.g. `/make-payment`) stay on v1 until the user hits an entry path.
 */
export function isV1PortalEntryPath(pathname: string): boolean {
  const path = pathname.trim();
  return path === "" || path === "/" || path === "/dashboard";
}

/** Auth0 logout returnTo — v2 preference lands on `/v2/`, not classic `/`. */
export function portalLogoutReturnTo(): string {
  if (getPreferredPortalVersion() === "v2") {
    return portalV2EntryUrl();
  }
  return portalOrigin();
}

/** Stays true for this page lifetime after a preference redirect starts. */
let portalV2RedirectStarted = false;

function shouldRedirectToPreferredPortalV2(pathname: string): boolean {
  if (!portalVersionAutoRedirect || isImpersonating()) {
    return false;
  }
  if (hasAuth0CallbackParams() || hasInvitationCode()) {
    return false;
  }
  if (!isV1PortalEntryPath(pathname)) {
    return false;
  }
  if (getPreferredPortalVersion() !== "v2") {
    return false;
  }
  if (shouldSkipPortalRedirect()) {
    return false;
  }
  return true;
}

/**
 * If the user prefers v2 and lands on a v1 entry route, send them to v2.
 * Returns true when a redirect was started or is in flight (do not render UI).
 * Safe before Auth0: skips callback query params and invitationCode.
 */
export function maybeRedirectToPreferredPortalV2(pathname: string): boolean {
  if (portalV2RedirectStarted) {
    return true;
  }
  if (!shouldRedirectToPreferredPortalV2(pathname)) {
    return false;
  }
  portalV2RedirectStarted = true;
  markPortalRedirect();
  window.location.replace(portalV2PreferenceRedirectUrl());
  return true;
}
